import { NotificationSeverity } from '@/components/notifications/Notification';
import { Option } from '@/components/inputs/AutocompleteInput';
import { ProcessDriverComplianceResponse } from '../../models';
import { useScannerStorage } from './useScannerStorage';
import { useDriverSearch } from './useDriverSearch';
import { useNavigate } from 'react-router-dom';
import { useState, useCallback} from 'react';
import { useNotification } from '@/hooks';
import { getScannedCode } from '@/utils/scannedCode';
import {
  useValidateReturnReceptionMutation,
  useProcessDriverComplianceMutation,
} from '../../services/returnsIntake.endpoints';

export const useReturnsScanner = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    driver,
    setDriver,
    scannedShippings,
    setScannedShippings,
    clearStorage
  } = useScannerStorage();

  const {
    setDriverSearch,
    driverOptions,
    isDriversLoading
  } = useDriverSearch(driver);

  const [inputValue, setInputValue] = useState('');
  const [isScannerActive, setIsScannerActive] = useState(true);
  const [failedReasons, setFailedReasons] = useState<Record<number, string>>({});

  const [validateReturnReception, { isLoading: isValidating }] = useValidateReturnReceptionMutation();
  const [processDriverCompliance, { isLoading: isProcessingBulk }] = useProcessDriverComplianceMutation();

  const [pendingDriver, setPendingDriver] = useState<Option | null>(null);

  const handleSetDriver = useCallback((option: Option | null) => {
    if (!option) {
      if (scannedShippings.length === 0) setDriver(null);
      return;
    }
    if (option.data === driver?.data) return;
    setPendingDriver(option);
  }, [driver, scannedShippings.length, setDriver]);

  const confirmDriver = useCallback(async () => {
    if (pendingDriver) {
      setScannedShippings([]);
      setFailedReasons({});
      setDriver(pendingDriver);
      setPendingDriver(null);
    }
  }, [pendingDriver, setDriver, setScannedShippings]);

  const cancelDriver = useCallback(() => {
    setPendingDriver(null);
  }, []);

  const handleRemove = useCallback((shippingNumber: number) => {
    setScannedShippings(prev => prev.filter(shipping => shipping.shippingNumber !== shippingNumber));
    setFailedReasons(prev => Object.fromEntries(
      Object.entries(prev).filter(([number]) => Number(number) !== shippingNumber)
    ));
  }, [setScannedShippings]);

  const handleScan = useCallback(async (code: string) => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    const scanned = getScannedCode(trimmedCode);
    if (!scanned) {
      showNotification('Código inválido', NotificationSeverity.error);
      return;
    }
    const shippingCode = scanned.code;
    const shippingCodeMode = scanned.mode;

    if (!driver?.data) {
      showNotification('Debe seleccionar un conductor antes de escanear', NotificationSeverity.warning);
      return;
    }

    const isAlreadyScanned = scannedShippings.some(
      shipping => String(shipping.shippingNumber) === shippingCode || shipping.commerceShippingNumber === shippingCode
    );

    if (isAlreadyScanned) {
      showNotification('Este pedido ya fue escaneado en esta sesión', NotificationSeverity.warning);
      return;
    }

    try {
      const result = await validateReturnReception({
        driverId: driver.data as string,
        code: shippingCode,
        mode: shippingCodeMode,
      }).unwrap();
      if (scannedShippings.some(shipping => shipping.shippingNumber === result.shippingNumber)) {
        showNotification('Este pedido ya fue escaneado en esta sesión', NotificationSeverity.warning);
        return;
      }
      setScannedShippings(prev => [result, ...prev]);
      showNotification('Pedido agregado a la rendición', NotificationSeverity.success);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      const message = error.data?.message || error.message || 'Error al validar el pedido';
      showNotification(message, NotificationSeverity.error);
    }
  }, [driver, scannedShippings, validateReturnReception, showNotification, setScannedShippings]);

  const handleReturn = useCallback(async () => {
    if (!driver?.data) return;
    try {
      const result = await processDriverCompliance({
        driverId: driver.data as string,
        shippingNumbers: scannedShippings.map(shipping => shipping.shippingNumber),
      }).unwrap();

      showNotification(result.message, NotificationSeverity.success);
      navigate(`/business/returns-intake/scanner/summary/${driver.data}`);

      clearStorage();
    } catch (err) {
      const error = err as { data?: Partial<ProcessDriverComplianceResponse>; message?: string };
      const failed = error.data?.failed ?? [];
      setFailedReasons(Object.fromEntries(failed.map(item => [item.shippingNumber, item.reason])));
      const message = error.data?.message || error.message || 'Error al procesar la rendición';
      showNotification(message, NotificationSeverity.error);
    }
  }, [driver, scannedShippings, processDriverCompliance, showNotification, navigate, clearStorage]);

  return {
    driver,
    setDriver: handleSetDriver,
    setDriverSearch,
    driverOptions,
    isDriversLoading,
    scannedShippings,
    failedReasons,
    inputValue,
    setInputValue,
    isScannerActive,
    setIsScannerActive,
    isSearchingShipping: false,
    isProcessing: isValidating || isProcessingBulk,
    handleScan,
    handleRemove,
    handleReturn,
    pendingDriver,
    confirmDriver,
    cancelDriver,
  };
};
